import { useCallback, useEffect, useRef, useState } from 'react';
import {
  canUseWebCodecs,
  decodeChunk,
  frameWithinAt,
  readSampleTable,
} from '@src/services/mediaExtractor';
import { joinMediaFile } from '@src/services/mediaFile';

// Same height as the strip, so a sharpened tile and its neighbours look alike.
const FRAME_HEIGHT = 120;

// Frames held from earlier views. Scrubbing back over ground already decoded
// should not decode it again; past this the oldest go. 600 frames at the
// measured ~4KB apiece is about 2.4MB.
const MAX_KEPT_FRAMES = 600;

// Zoom and scroll arrive as a stream of values, and a drag emits dozens.
// Decoding for an intermediate one is wasted work.
const SETTLE_MS = 200;

// How often to look for the chunk holding a tile. The loader is already fetching
// it first, so this is a short wait, not a poll of something far off.
const CHUNK_POLL_MS = 80;

const EMPTY = { times: [], urls: [] };

/**
 * The exact frame for every tile on screen, once a tile is finer than the strip
 * the video was sampled into.
 *
 * The strip holds one frame a second, which IS the frame playing for any tile at
 * or above 1s. Below that a tile falls between strip frames — at full zoom a
 * tile spans 0.356s, so most tiles stand for a moment the strip has no frame
 * for and would otherwise show a skeleton.
 *
 * The only route to a frame between two others is to decode the run from the
 * keyframe before it, and that route is cheap here: the chunk holding it is
 * already on its way, because the loader fetches what the visible tiles are
 * waiting for first. Measured in Chrome, a whole screenful at full zoom is 79ms
 * of decoding and no request of its own.
 *
 * Returns `{ times, urls }`, ascending and paired. It is laid over the strip, so
 * it only fills tiles in, never replaces a frame already shown with a different
 * one. Every failure — no WebCodecs, a file that will not demux, a chunk that
 * never arrives — leaves the strip standing.
 */
const useExactFrames = ({
  videoUrl,
  fromSec,
  toSec,
  tileSpanSec,
  stripSpacingSec,
}) => {
  const [frames, setFrames] = useState(EMPTY);
  // Everything decoded so far, by the moment it stands for. A ref rather than
  // state so accumulating does not depend on the previous render.
  const keptRef = useRef(new Map());

  const publish = useCallback(() => {
    const sorted = [...keptRef.current.keys()]
      .map(Number)
      .sort((a, b) => a - b);
    setFrames({
      times: sorted,
      urls: sorted.map((seconds) => keptRef.current.get(seconds.toFixed(3))),
    });
  }, []);

  const active =
    Boolean(videoUrl) &&
    canUseWebCodecs() &&
    tileSpanSec > 0 &&
    stripSpacingSec > 0 &&
    tileSpanSec < stripSpacingSec;

  useEffect(() => {
    if (!active) return undefined;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const joined = joinMediaFile(videoUrl);
      // No download of this file running means nothing to read: the strip came
      // from cache and never started one. Joining without a plan cannot start
      // it, so there is nothing to sharpen from.
      if (!joined) return;
      const state = { dirty: false };
      const take = (second, blob) => {
        if (controller.signal.aborted) return;
        const kept = keptRef.current;
        const key = second.toFixed(3);
        if (kept.has(key)) return;
        kept.set(key, URL.createObjectURL(blob));
        // Map iterates in insertion order, so the oldest fall off first.
        while (kept.size > MAX_KEPT_FRAMES) {
          const oldest = kept.keys().next().value;
          URL.revokeObjectURL(kept.get(oldest));
          kept.delete(oldest);
        }
        state.dirty = true;
      };

      try {
        const table = await readSampleTable(videoUrl);
        if (controller.signal.aborted) return;

        // One moment per tile, at the middle — the same moment the lane uses to
        // place a frame. Only the ones the pool cannot already answer, by the
        // same test the tiles use.
        const held = [...keptRef.current.keys()]
          .map(Number)
          .sort((a, b) => a - b);
        const wanted = [];
        for (
          let sec = fromSec + tileSpanSec / 2;
          sec < toSec;
          sec += tileSpanSec
        ) {
          if (frameWithinAt(held, sec, tileSpanSec / 2) < 0) wanted.push(sec);
        }
        if (!wanted.length) return;

        // Put these at the head of the loader's queue, then take each chunk as
        // it lands. It is already fetching nearest-first, so this is usually a
        // wait of one chunk.
        joined.setView(fromSec, toSec, wanted);
        const chunks = joined.chunks
          .map((chunk, index) => ({ chunk, index }))
          .filter(
            ({ chunk }) => chunk.toSec > fromSec && chunk.fromSec < toSec,
          );

        // eslint-disable-next-line no-restricted-syntax
        for (const { chunk, index } of chunks) {
          if (controller.signal.aborted) break;
          const seconds = wanted.filter(
            (sec) => sec >= chunk.fromSec && sec < chunk.toSec,
          );
          if (!seconds.length) continue; // eslint-disable-line no-continue
          if (!joined.held.has(index)) {
            // eslint-disable-next-line no-await-in-loop
            const arrived = await new Promise((resolve) => {
              const poll = setInterval(() => {
                if (controller.signal.aborted) {
                  clearInterval(poll);
                  resolve(false);
                } else if (joined.held.has(index)) {
                  clearInterval(poll);
                  resolve(true);
                }
              }, CHUNK_POLL_MS);
            });
            if (!arrived) break;
          }
          // eslint-disable-next-line no-await-in-loop
          await decodeChunk({
            table,
            store: joined.store,
            chunk,
            seconds,
            height: FRAME_HEIGHT,
            signal: controller.signal,
            onFrame: take,
          });
          if (state.dirty && !controller.signal.aborted) {
            state.dirty = false;
            publish();
          }
        }
      } catch (error) {
        // The strip is already correct to within a second; failing to sharpen it
        // is not worth telling the user about.
      } finally {
        joined.release();
        if (state.dirty && !controller.signal.aborted) publish();
      }
    }, SETTLE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [active, videoUrl, fromSec, toSec, tileSpanSec, publish]);

  // The pool belongs to one video, not to a zoom level: zooming out and back is
  // constant, and handing it back each time would decode it all again.
  useEffect(() => {
    setFrames(EMPTY);
    return () => {
      keptRef.current.forEach((url) => URL.revokeObjectURL(url));
      keptRef.current = new Map();
    };
  }, [videoUrl]);

  // Withheld rather than dropped while zoomed out: half of a 53s tile would let
  // a frame from 26s away stand in, far worse than the strip frame already
  // there.
  return active ? frames : EMPTY;
};

export default useExactFrames;
